import { z } from 'zod';
import { isoDateTime } from '../dokless/tracking-v2.schema.js';
import { carrierSearchKeySchema, readFailureSchema } from '../enums.js';
import { MAX_NUMBERS_PER_READ, RECIPE_SCHEMA, recipeDocumentSchema, recipeResultsSchema, recipeStepSchema, } from '../recipes/recipe.schema.js';
// Extension ↔ API protocol (01-architecture.md, rule 2): the extension talks to
// the API only through these shapes. No carrier-specific code: carrier
// knowledge travels as data (recipe steps, start URL, hints).
export const EXTENSION_PROTOCOL_VERSION = '2.0';
export const extensionClientStateSchema = z.enum(['idle', 'busy', 'waiting_human']);
/** `POST /extension/heartbeat`. */
export const heartbeatRequestSchema = z.object({
    client_name: z.string().min(1).max(32),
    version: z.string().min(1).max(32),
    state: extensionClientStateSchema,
    current_job_id: z.string().nullable().optional(),
    chrome_profile: z.string().max(64).nullable().optional(),
});
export const heartbeatResponseSchema = z.object({
    paused: z.boolean(),
    /** The API advertises the minimum client version; older clients are told to update. */
    min_version: z.string(),
    server_time: isoDateTime,
});
/** The recipe a `recipe`-mode job carries (`plancia.recipe/v3`, `src/recipes`). */
export const recipeSchema = z.object({
    id: z.string(),
    version: z.int().min(1),
    schema: z.literal(RECIPE_SCHEMA),
    steps: z.array(recipeStepSchema).min(1),
    reference_types: recipeDocumentSchema.shape.reference_types.optional(),
    results: recipeResultsSchema.optional(),
    /** Text the site shows when the reference does not exist. */
    not_found_text: z.string().nullable().optional(),
});
export const extensionJobSchema = z.object({
    job_id: z.string(),
    read_id: z.string(),
    shipment_id: z.string(),
    carrier: z.object({ code: z.string(), name: z.string() }),
    search: z.object({ key: carrierSearchKeySchema, value: z.string() }),
    /**
     * A recipe with `for_each_number`: every number to search in this visit, in order, `search` first
     * (owner, 20/10; ADR 0068). Each goes in the field of its key; `pair` is the number typed beside it when
     * its kind has `pair_with` (Akkon: the booking, else the B/L, with a container; ADR 0095).
     */
    searches: z
        .array(z.object({
        key: carrierSearchKeySchema,
        value: z.string(),
        pair: z.object({ key: z.enum(['booking', 'bl']), value: z.string() }).optional(),
    }))
        .max(MAX_NUMBERS_PER_READ)
        .optional(),
    mode: z.enum(['recipe', 'model']),
    start_url: z.string().nullable(),
    recipe: recipeSchema.nullable(),
    /** Goal given to the navigation model in `model` mode. */
    model_goal: z.string().nullable(),
    /** Free hints learned about the site (v1 `indicazioni`). */
    hints: z.array(z.string()).default([]),
    /**
     * Per-shipment values for the recipe placeholders other than `{reference}`
     * (v1 `variabili`): `choice` fills `{choice}` (Akkon's country of loading).
     * They never override `reference` or `reference_type`.
     */
    variables: z.record(z.string(), z.string()).optional(),
    deadline_at: isoDateTime,
});
/** `POST /extension/claim`: long-poll for the next job (≤ 20 s). */
export const claimRequestSchema = z.object({
    client_name: z.string().min(1).max(32),
    version: z.string().min(1).max(32),
    wait_seconds: z.int().min(0).max(20).default(20),
});
export const claimResponseSchema = z.object({
    job: extensionJobSchema.nullable(),
});
/** What the extension captured on the page; big parts are base64/text and go to the blob store. */
export const captureBundleSchema = z.object({
    html: z.string().nullable().optional(),
    text: z.string().nullable().optional(),
    tables: z
        .array(z.array(z.array(z.string())))
        .nullable()
        .optional(),
    network: z
        .array(z.object({ url: z.string(), status: z.int(), body: z.string() }))
        .nullable()
        .optional(),
    screenshot_jpeg_base64: z.string().nullable().optional(),
});
/** What one number of a `for_each_number` visit gave. */
export const numberResultSchema = z.object({
    key: carrierSearchKeySchema,
    value: z.string(),
    /** `wall`: a captcha or anti-bot stopped the visit here; `skipped`: not searched (after a wall). */
    outcome: z.enum(['captured', 'not_found', 'failed', 'wall', 'skipped']),
    failure: readFailureSchema.nullable().optional(),
    failure_detail: z.string().max(4000).nullable().optional(),
    failed_step: z.int().min(0).nullable().optional(),
    url: z.string().nullable().optional(),
    capture: captureBundleSchema.nullable().optional(),
});
/** `POST /extension/jobs/{job_id}/result`. */
export const jobResultSchema = z.object({
    job_id: z.string(),
    client_name: z.string(),
    outcome: z.enum(['captured', 'failed']),
    url: z.string().nullable(),
    started_at: isoDateTime,
    finished_at: isoDateTime,
    failure: readFailureSchema.nullable().optional(),
    failure_detail: z.string().max(4000).nullable().optional(),
    /** Step index where a recipe stopped (for `recipe_broken`). */
    failed_step: z.int().min(0).nullable().optional(),
    capture: captureBundleSchema.nullable(),
    /**
     * A `for_each_number` visit: one entry per number of `searches`, in order. The first is also the result's
     * own `capture`/`failure` (an older API reads only those).
     */
    searches: z.array(numberResultSchema).max(MAX_NUMBERS_PER_READ).optional(),
    /** Moves taken in model mode, to learn a recipe. */
    model_steps: z.array(z.record(z.string(), z.unknown())).nullable().optional(),
});
export const jobResultAcceptedSchema = z.object({
    read_id: z.string(),
    accepted: z.literal(true),
});
/**
 * `POST /extension/recipes/draft`: a recipe taught in the recorder (S27c),
 * stored as a `draft` version that a person reviews and activates in S12.
 */
export const recipeDraftRequestSchema = z.object({
    client_name: z.string().min(1).max(32),
    carrier_code: z.string().trim().min(1).max(64),
    /** A `plancia.recipe/v3` document (validated by the API). */
    recipe: z.record(z.string(), z.unknown()),
});
export const recipeDraftResponseSchema = z.object({
    carrier_code: z.string(),
    version: z.int().min(1),
    status: z.literal('draft'),
    warnings: z.array(z.object({ path: z.string(), message: z.string() })),
});
//# sourceMappingURL=job.schema.js.map